/**
 * Storage API do marketdata — CRUD HTTP de /api/storage (defaults, settings,
 * desktops, templates, rating, poll) + /api/specs. Extraído de marketdata.jsx
 * (bloco l.2573-2960) na modularização dos god-objects; corpos preservados
 * byte-a-byte, apenas `this` → `md` (a instância Marketdata, passada pelo
 * delegate 1-linha da classe). Contrato pinado por
 * __tests__/marketdataStorageContract.test.js — escrito ANTES da extração.
 *
 * Políticas de erro (INTENCIONALMENTE distintas por método — não "arrumar"):
 * - saveDefaults/saveSettings/saveRating/savePoll/saveSpecs: PROPAGAM (caller trata)
 * - getDefaults/getSettings/getRating/getPoll: erro → null (primeira vez é esperado)
 * - desktop/template CRUD: utilities.errorHandler (toast) e resolve undefined
 */
import * as utilities from "./utilites.jsx";

const STORAGE_COMMAND = "/api/storage";

export const saveDefaults = async (md, content) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "defaults",
    chartid: 1, // Use fixed ID 1 for indicator defaults (user-specific, not chart-specific)
    name: "defaults",
    content: content, // Already a string from the store
  };
  console.log("[Marketdata] Saving defaults with request:", {
    url,
    kind: request.kind,
    chartid: request.chartid,
    name: request.name,
    contentLength: content.length,
    contentPreview: content.substring(0, 100),
  });
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
    return null;
  } catch (error) {
    console.error("[Marketdata] Failed to save defaults:", error.message);
    console.error("[Marketdata] Request was:", request);
    throw error; // Re-throw so store can handle it
  }
};

export const getDefaults = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=defaults&chartid=1&name=defaults&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      const dt = r.data;
      if (typeof dt === "string") {
        return JSON.parse(dt);
      } else if (dt.content) {
        return JSON.parse(dt.content);
      }
      return dt;
    }
    return null;
  } catch {
    // Return null instead of showing error - this is expected on first use
    console.log("[Marketdata] No defaults found on server (likely first time)");
    return null;
  }
};

export const saveSettings = async (md, content) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "settings",
    chartid: 1, // Use fixed ID 1 for user settings
    name: "settings",
    content: content,
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
    return null;
  } catch (error) {
    console.error("[Marketdata] Failed to save settings:", error.message);
    throw error;
  }
};

export const getSettings = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=settings&chartid=1&name=settings&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      const dt = r.data;
      if (typeof dt === "string") {
        return JSON.parse(dt);
      } else if (dt.content) {
        return JSON.parse(dt.content);
      }
      return dt;
    }
    return null;
  } catch {
    console.log("[Marketdata] No settings found on server (likely first time)");
    return null;
  }
};

export const saveDesktop = async (md, id, _name, _content) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "desktop",
    chartid: id,
    name: _name,
    content: _content,
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const getDesktopList = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=desktop&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const getDesktop = async (md, id) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=desktop&chartid=${id}&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const deleteDesktop = async (md, id) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=desktop&chartid=${id}`;
  try {
    const resp = await utilities.deleteFromURL(url, md.signalRToken);
    console.log(resp);
  } catch (error) {
    utilities.errorHandler(error);
  }
};

// Template storage methods
export const saveTemplate = async (md, id, _name, _content) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "template",
    chartid: id,
    name: _name,
    content: _content,
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const getTemplateList = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=template&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const getTemplate = async (md, id) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=template&chartid=${id}&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const deleteTemplate = async (md, id) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=template&chartid=${id}`;
  try {
    const resp = await utilities.deleteFromURL(url, md.signalRToken);
    console.log(resp);
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const getTemplates = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=templates&chartid=1&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      const dt = r.data;
      if (typeof dt === "string") {
        return JSON.parse(r.data);
      } else {
        return JSON.parse(r.data.content);
      }
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const saveTemplates = async (md, _content) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "templates",
    chartid: 1,
    name: "templates",
    content: JSON.stringify(_content),
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      return r.data;
    }
  } catch (error) {
    utilities.errorHandler(error);
  }
};

export const deleteTemplates = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=templates&chartid=1`;
  try {
    const resp = await utilities.deleteFromURL(url, md.signalRToken);
    console.log(resp);
  } catch (error) {
    utilities.errorHandler(error);
  }
};

// Rating storage methods
export const saveRating = async (md, ratingData) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "rating",
    chartid: 1, // Use fixed ID 1 for rating (user-specific)
    name: "rating",
    content: JSON.stringify(ratingData),
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      console.log("[Marketdata] Rating saved successfully");
      return r.data;
    }
    return null;
  } catch (error) {
    console.error("[Marketdata] Failed to save rating:", error.message);
    throw error;
  }
};

export const getRating = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=rating&chartid=1&name=rating&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      const dt = r.data;
      if (typeof dt === "string") {
        return JSON.parse(dt);
      } else if (dt.content) {
        return JSON.parse(dt.content);
      }
      return dt;
    }
    return null;
  } catch {
    // Return null - this is expected if user hasn't rated yet
    console.log(
      "[Marketdata] No rating found on server (user has not rated yet)",
    );
    return null;
  }
};

// Poll storage methods
export const savePoll = async (md, pollData) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}`;
  const request = {
    kind: "poll",
    chartid: 1,
    name: "poll",
    content: JSON.stringify(pollData),
  };
  try {
    const resp = await utilities.postJsonToURL(url, request, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      console.log("[Marketdata] Poll saved successfully");
      return r.data;
    }
    return null;
  } catch (error) {
    console.error("[Marketdata] Failed to save poll:", error.message);
    throw error;
  }
};

export const getPoll = async (md) => {
  const url = `${md.getUrl()}${STORAGE_COMMAND}?kind=poll&chartid=1&name=poll&_=${new Date().getTime()}`;
  try {
    const resp = await utilities.getStringFromURL(url, md.signalRToken);
    const r = JSON.parse(resp);
    if (r.status && r.status === "ok") {
      const dt = r.data;
      if (typeof dt === "string") return JSON.parse(dt);
      if (dt.content) return JSON.parse(dt.content);
      return dt;
    }
    return null;
  } catch {
    console.log(
      "[Marketdata] No poll found on server (user has not voted yet)",
    );
    return null;
  }
};

// Client specs — posts to the dedicated /api/specs endpoint (not /api/storage).
// We use a typed collection server-side (client_specs) so fields are queryable;
// stuffing a JSON blob into storage would defeat that. Call is fire-and-forget
// from authStore — never await on it during login.
export const saveSpecs = async (md, report) => {
  const url = `${md.getUrl()}/api/specs`;
  const resp = await utilities.postJsonToURL(url, report, md.signalRToken);
  const r = JSON.parse(resp);
  if (r.status === "ok") {
    console.log(`[Marketdata] Specs saved (${r.result})`);
    return r.result;
  }
  throw new Error(r.message || "Failed to save specs");
};
